import { useEffect, useRef, useState } from "react";
import { RootStackParamList } from "../navigations/RootNavigation";
import { StackScreenProps } from "@react-navigation/stack";
import { useNavigation } from "@react-navigation/core";

type UseScreenStateNavigation = StackScreenProps<RootStackParamList>;

type ObjectWithValue = {
    value: string;
};

// Order-insensitive content signature of a selection. Used to detect when the *default*
// selection actually changed by content, as opposed to merely receiving a fresh array
// reference on re-render (which the callers do — see the effect-dep note below).
const valuesKey = (items: readonly ObjectWithValue[]) =>
    items
        .map((i) => i.value)
        .sort()
        .join(",");

/**
 * Acts exactly like `useState()`, but additionally appends the state to the navigation url.
 * Only accepts arrays containing objects that have at least a `value` property.
 */
export const useScreenState = <T extends ObjectWithValue[]>(key: string, initialValue: T) => {
    const [state, setState] = useState<T>(initialValue);
    const navigation = useNavigation<UseScreenStateNavigation["navigation"]>();

    // `initialValue` is the default selection. It can legitimately change after mount when data
    // loads and new options appear (e.g. signing a mission adds a contract state that was not in
    // the list yet). When that happens and the user is still sitting on the previous default
    // (has not narrowed the filter), follow the new default so the freshly-available items are
    // not frozen out. Without this, the stale `state` is mirrored into the route param and hides
    // the new items — the "signed mission disappears" bug (BAS-2806). Detection compares content
    // signatures, never references, so a same-content re-render does not trigger it. Done via
    // the "adjusting state on prop change" pattern (https://react.dev/learn/you-might-not-need-an-effect),
    // same as `Dropdown`'s `DropdownItemsList`.
    const defaultKey = valuesKey(initialValue);
    const [prevDefaultKey, setPrevDefaultKey] = useState(defaultKey);
    if (defaultKey !== prevDefaultKey) {
        const wasOnDefault = valuesKey(state) === prevDefaultKey;
        setPrevDefaultKey(defaultKey);
        if (wasOnDefault) {
            setState(initialValue);
        }
    }

    // Compute the param value during render so the effect dep is a primitive
    // string (or undefined). Using `state` / `initialValue` directly as deps
    // would re-fire the effect whenever the caller passes a fresh-reference
    // `initialValue` even if its content is unchanged — which infinite-loops
    // when setParams triggers a re-render that rebuilds the array upstream.
    const hasCustomParams =
        state.length !== initialValue.length ||
        state.some((p) => !initialValue.find((i) => i.value === p.value));
    const paramsValue = hasCustomParams ? state.map((f) => f.value).join(",") : undefined;

    // Mirror state into navigation params as a post-commit effect. Doing the
    // `navigation.setParams` call inside the `setState` updater (or even inside
    // an event handler that's been deferred via a functional update) runs it
    // during React's render/commit phase, which triggers
    // "Cannot update a component while rendering a different component".
    const isInitialMount = useRef(true);
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        navigation.setParams({ [key]: paramsValue });
    }, [paramsValue, key, navigation]);

    return [state, setState] as const;
};
